import { createAsyncThunk } from '@reduxjs/toolkit';
import { CancelToken } from 'axios';
import { Source } from '../../shared/enums/source';
import { TrialExtendSource } from '../../shared/enums/trial-extend-source';
import { ResponseErrorWithHandled, ResponseSuccess } from '../../shared/types';
import {
  getFirstSequence,
  getSampleSequence,
} from '../sequence/helpers/sequence.api';
import {
  CreateSequenceRequestPayload,
  UpdateUserSettingsRequestPayload,
} from '../sequence/types';
import {
  getUserSettings,
  getUserSubscriptionMetadata,
  updateUserSettings,
  upgradeOnboardingStep,
  getUserNotifications,
  getUnreadNotifications,
  getUnreadEmailThreadsCount,
  markNotificationsAsRead,
  createSequence,
  compareAuthToken,
  getAgencyConfig,
  getPostLoadMeta,
  extendTrialPeriod,
  getPollingNotifications,
  updateHiddenNotification,
  exploreDemoAccount,
  updateUserFeatureEngagement,
  updateLeadFinderVersion,
} from './helpers/meta-api';
import {
  CompareAuthTokenPayload,
  UnreadEmailThreadsCountPayload,
  UpdateUserFeatureEngagementPayload,
  UpgradeOnboardingStepPayload,
} from './types';
import { getCoreFeaturesCredits } from './helpers/credits-api';
import { resolvePaymentApi } from '../settings/components/billing-subscription/helpers/payment-provider';
import { getPaddleSubscriptionMetadata } from '../settings/components/billing-subscription/helpers/billing-subscription.api';

type CreateSequenceType = CreateSequenceRequestPayload & { source: Source };

export const getUserSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/getUserSettingsRequest', async (args, thunkAPI) => {
  try {
    return await getUserSettings();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getUserSubscriptionMetadataRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/getUserSubscriptionMetadataRequest', async (args, thunkAPI) => {
  try {
    console.log('getUserSubscriptionMetadataRequest');
    return await resolvePaymentApi(
      thunkAPI.getState,
      () => getPaddleSubscriptionMetadata(),
      () => getUserSubscriptionMetadata(),
    )
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getPostLoadMetaRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/getPostLoadMetaRequest', async (args, thunkAPI) => {
  try {
    return await getPostLoadMeta();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getAgencyConfigRequest = createAsyncThunk<
  ResponseSuccess,
  any,
  { rejectValue: ResponseErrorWithHandled }
>('home/getAgencyConfigRequest', async (args, thunkAPI) => {
  try {
    return await getAgencyConfig(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const updateUserSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateUserSettingsRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('home/updateUserSettingsRequest', async ({ ...rest }, thunkAPI) => {
  try {
    return await updateUserSettings(rest);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const getFirstSequenceRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/getFirstSequenceRequest', async (_, thunkAPI) => {
  try {
    return await getFirstSequence();
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const compareAuthTokenRequest = createAsyncThunk<
  ResponseSuccess,
  CompareAuthTokenPayload,
  { rejectValue: ResponseErrorWithHandled }
>('home/compareAuthTokenRequest', async (args, thunkAPI) => {
  try {
    return await compareAuthToken(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const getSampleSequenceRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/getSampleSequenceRequest', async (_, thunkAPI) => {
  try {
    return await getSampleSequence();
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const upgradeOnboardingStepRequest = createAsyncThunk<
  ResponseSuccess,
  UpgradeOnboardingStepPayload,
  { rejectValue: ResponseErrorWithHandled }
>('home/upgradeOnboardingStepRequest', async (args, thunkAPI) => {
  try {
    return await upgradeOnboardingStep(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const getUserNotificationsRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('home/userNotifications', async (args, thunkAPI) => {
  try {
    return await getUserNotifications(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const getUserUnreadNotificationsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/userUnreadNotification', async (args, thunkAPI) => {
  try {
    return await getUnreadNotifications();
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const getUnreadEmailThreadsCountRequest = createAsyncThunk<
  ResponseSuccess,
  UnreadEmailThreadsCountPayload,
  { rejectValue: ResponseErrorWithHandled }
>('home/getUnreadEmailThreadsCountRequest', async (args, thunkAPI) => {
  try {
    return await getUnreadEmailThreadsCount(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const markNotificationsAsReadRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('home/markNotificationAsRead', async (args, thunkApi) => {
  try {
    return await markNotificationsAsRead(args);
  } catch (err) {
    return thunkApi.rejectWithValue(err);
  }
});

export const createSequenceFromGettingStartedRequest = createAsyncThunk<
  ResponseSuccess,
  CreateSequenceType,
  { rejectValue: ResponseErrorWithHandled }
>(
  'sequence/createSequenceFromGettingStartedRequest',
  async ({ source, ...rest }, thunkAPI) => {
    try {
      return await createSequence(source, rest);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const extendTrialPeriodRequest = createAsyncThunk<
  ResponseSuccess,
  TrialExtendSource,
  { rejectValue: ResponseErrorWithHandled }
>('sequence/extendTrialPeriodRequest', async (args, thunkAPI) => {
  try {
    return await extendTrialPeriod(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const getCoreFeaturesCreditsRequest = createAsyncThunk(
  'home/fetchCoreFeaturesCredits',
  async (_, thunkAPI) => {
    // No parameter now
    try {
      const response = await getCoreFeaturesCredits(); // Adjust API call accordingly
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const getPollingNotificationsRequest = createAsyncThunk<
  ResponseSuccess,
  CancelToken,
  { rejectValue: ResponseErrorWithHandled }
>('home/getPollingNotifications', async (cancelToken, thunkAPI) => {
  try {
    return await getPollingNotifications(cancelToken);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const updateHiddenNotificationRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('home/updateHiddenNotification', async (notificationId, thunkAPI) => {
  try {
    return await updateHiddenNotification(notificationId);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const exploreDemoAccountRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('home/exploreDemoAccount', async (_, thunkAPI) => {
  try {
    return await exploreDemoAccount();
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const updateUserFeatureEngagementRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateUserFeatureEngagementPayload,
  { rejectValue: ResponseErrorWithHandled }
>('home/updateUserFeatureEngagementRequest', async (args, thunkAPI) => {
  try {
    return await updateUserFeatureEngagement(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const updateLeadFinderVersionRequest = createAsyncThunk<
  ResponseSuccess,
  'v1' | 'v2',
  { rejectValue: ResponseErrorWithHandled }
>('home/updateLeadFinderVersionRequest', async (version, thunkAPI) => {
  try {
    return await updateLeadFinderVersion(version);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});
